import useTalentTreeBlueprint from '@resources/js/contexts/useTalentTreeBlueprint';
import useTheme from '@resources/js/contexts/useTheme';
import { talentTreeDifferences } from '@resources/js/helpers/buildsTalentTrees';
import {
  type RetailWarcraftTalentTreeBlueprint,
  type RetailWarcraftTalentTreeBuildNode,
  RetailWarcraftTalentTreeNodeType,
  RetailWarcraftTalentTreeType,
} from '@resources/js/typings/viewModels/retailWarcraftTalentTreeBuildNode';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState, type JSX } from 'react';
import { BuildsMetricTile } from '../BuildsMetricTiles/BuildsMetricTiles';
import {
  BuildsTalentTreeAlternateClassTreesTable,
  type BuildsTalentTreeBuildSectionProps,
  type BuildsTalentTreeBuildSetViewModel,
  useTreeSelection,
} from '../BuildsTalentTreeBuildSection/BuildsTalentTreeBuildSection';
import Button from '../Button/Button';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import Heading from '../Heading/Heading';
import HorizontalContent from '../HorizontalContent/HorizontalContent';
import TalentTreeFull, {
  type TalentTreeFullProps,
} from '../TalentTreeFull/TalentTreeFull';
import TalentTreeNode from '../TalentTreeNode/TalentTreeNode';
import VerticalContent from '../VerticalContent/VerticalContent';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import { BuildsLargeLinkButton } from '@resources/js/components/BuildsLargeLinkButton/BuildsLargeLinkButton';
import Icon from '@resources/js/components/Icon/Icon';
import Tooltip from '@resources/js/components/Tooltip/Tooltip';

export type BuildsAlternativeTalentTreeBuildsSectionViewModel = {
  parent: BuildsTalentTreeBuildSectionProps;
  alternativeBuildsLabel: string;
  alternativeBuildsDescription: string;
  alternativeBuildLabel: string;
  showClassTreesLabel: string;
  classTalentsLabel: string;
  specTalentsLabel: string;
  heroTalentsLabel: string;
  noneLabel: string;
  showFullTreeLabel: string;
  hideFullTreeLabel: string;
  talentTreeLabel: string;
  alternativeTalentTreeLabel: string;
};

export default function BuildsAlternativeTalentTreeBuildsSection({
  parent,
  ...props
}: BuildsAlternativeTalentTreeBuildsSectionViewModel): JSX.Element {
  const {
    talentTreeBuildSets: [recommended, ...alternatives],
  } = parent;

  const [selectedTalentTree, setSelectedTalentTree] = React.useState(
    recommended?.alternatives.find((alt) => alt.isDefaultSelection)
      ?.talentTree || recommended?.alternatives[0]?.talentTree,
  );

  useEffect(() => {
    setSelectedTalentTree(
      recommended?.alternatives.find((alt) => alt.isDefaultSelection)
        ?.talentTree || recommended?.alternatives[0]?.talentTree,
    );
  }, [recommended]);

  const labels = {
    ...props,
    addedTalentsLabel: parent.addedTalentsLabel,
    removedTalentsLabel: parent.removedTalentsLabel,
    alternativesTitle: parent.alternativesTitle,
    alternativesDescription: parent.alternativesDescription,
    popularityLabel: parent.popularityLabel,
    currentlySelectedLabel: parent.currentlySelectedLabel,
    alternativeTreeTitleLabel: parent.alternativeTreeTitleLabel,
    openReportLabel: parent.openReportLabel,
    topLogLabel: parent.topLogLabel,
    maxKeyLabel: parent.maxKeyLabel,
    openReportButtonBackgroundImageUrl:
      parent.openReportButtonBackgroundImageUrl,
  };

  return (
    selectedTalentTree && (
      <AlternativeTalentTreeSectionInterior
        alternatives={alternatives}
        labels={labels}
        referenceTree={selectedTalentTree}
      />
    )
  );
}

type AlternativeBuildLabels = {
  classTalentsLabel: string;
  specTalentsLabel: string;
  heroTalentsLabel: string;
  showClassTreesLabel: string;
  addedTalentsLabel: string;
  removedTalentsLabel: string;
  noneLabel: string;
  showFullTreeLabel: string;
  hideFullTreeLabel: string;
  alternativesTitle: string;
  alternativesDescription: string;
  popularityLabel: string;
  currentlySelectedLabel: string;
  talentTreeLabel: string;
  alternativeTreeTitleLabel: string;
  openReportLabel: string;
  topLogLabel: string;
  maxKeyLabel: string;
  openReportButtonBackgroundImageUrl: string;
};

const baseClassName = 'builds-alternative-talent-tree-builds-section';

function elementClassName(name: string, ...modifiers: string[]): string {
  const el = `${baseClassName}__${name}`;
  return classNames(el, ...modifiers.map((mod) => `${el}--${mod}`));
}

const qualities = ['epic', 'rare', 'uncommon'] as const;

function AlternativeTalentTreeSectionInterior({
  alternatives,
  labels,
  referenceTree,
}: {
  labels: AlternativeBuildLabels & {
    alternativeBuildsLabel: string;
    alternativeBuildsDescription: string;
  };
  referenceTree: TalentTreeFullProps;
  alternatives: BuildsTalentTreeBuildSetViewModel[];
}): JSX.Element {
  return (
    <section className={baseClassName}>
      <VerticalContent>
        <EnhancedMarkdown
          markdown={`
# ${labels.alternativeBuildsLabel}

${labels.alternativeBuildsDescription}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        {alternatives.map((alternative, ix) => (
          <AlternativeBuild
            key={ix}
            quality={qualities[ix]}
            buildSet={alternative}
            labels={labels}
            referenceTree={referenceTree}
          />
        ))}
      </VerticalContent>
    </section>
  );
}

interface TreeDifferences {
  class: RetailWarcraftTalentTreeBuildNode[];
  spec: RetailWarcraftTalentTreeBuildNode[];
  hero: RetailWarcraftTalentTreeBuildNode[];
}

function splitNodeTrees(
  nodes: RetailWarcraftTalentTreeBuildNode[],
): TreeDifferences {
  return nodes.reduce(
    (map: TreeDifferences, node) => {
      switch (node.node.treeType) {
        case RetailWarcraftTalentTreeType.ClassTree:
          map.class.push(node);
          return map;
        case RetailWarcraftTalentTreeType.SpecTree:
          map.spec.push(node);
          return map;
        case RetailWarcraftTalentTreeType.HeroTree:
          map.hero.push(node);
          return map;
      }
    },
    {
      class: [],
      spec: [],
      hero: [],
    },
  );
}

function AlternativeBuild({
  buildSet,
  quality,
  referenceTree,
  labels,
}: {
  buildSet: BuildsTalentTreeBuildSetViewModel;
  referenceTree: TalentTreeFullProps;
  labels: AlternativeBuildLabels;
  quality: 'epic' | 'rare' | 'uncommon';
}): JSX.Element {
  const { title, metricTiles, alternatives } = buildSet;
  const theme = useTheme();
  const [showFull, setShowFull] = useState(false);
  const [showAlternateClassTrees, setShowAlternateClassTrees] = useState(false);
  const [selectedIndex, setSelectedIndex] = useTreeSelection(alternatives);

  const selected = alternatives[selectedIndex];

  const blueprint = useTalentTreeBlueprint(
    selected?.talentTree.dehydratedBuild.changeSet,
  );

  const { addedTalents, removedTalents } = useMemo(() => {
    if (!blueprint || !selected?.talentTree) {
      return {};
    }
    const { addedTalents, removedTalents } = talentTreeDifferences(
      blueprint,
      referenceTree,
      selected.talentTree,
    );

    return {
      addedTalents: splitNodeTrees(addedTalents),
      removedTalents: splitNodeTrees(removedTalents),
    };
  }, [referenceTree, selected?.talentTree, blueprint]);

  const numberOfMetricTilesAndButtons =
    metricTiles.length + 1 + (selected && selected.reportUrl ? 1 : 0);

  const heroTreeId = buildSet.alternatives
    .map((alt) => alt.talentTree.dehydratedBuild.heroSpecId)
    .at(0);

  const referenceHeroTreeId = referenceTree.dehydratedBuild.heroSpecId;

  const heroTreeBlueprint = blueprint?.heroTrees?.find(
    (tree) => tree.id === heroTreeId,
  );

  const includeHeroTreeColumn = heroTreeBlueprint !== undefined;

  return (
    <VerticalContent
      className={elementClassName('alternative-build', quality)}
      gap={'small'}
    >
      <Heading level={3}>{title}</Heading>
      <div
        className={elementClassName(
          'tiles',
          ...(numberOfMetricTilesAndButtons > 5 ? ['tiny-gap'] : []),
        )}
      >
        {metricTiles.map((tile, ix) => (
          <BuildsMetricTile {...tile} key={ix} valueClassName={quality} />
        ))}
        <Button
          styles={theme.primaryButtonStyles}
          onClick={() => setShowFull((state) => !state)}
          className={elementClassName('large-link-button')}
        >
          <span className={elementClassName('large-link-caption')}>
            {labels.talentTreeLabel}
          </span>
          <span className={elementClassName('large-link-text')}>
            {showFull ? labels.hideFullTreeLabel : labels.showFullTreeLabel}
          </span>
        </Button>
        {selected?.reportUrl ? (
          <BuildsLargeLinkButton
            caption={labels.topLogLabel}
            text={labels.openReportLabel}
            isExternal={true}
            href={selected.reportUrl}
            backgroundImageUrl={labels.openReportButtonBackgroundImageUrl}
          />
        ) : null}
      </div>
      {showFull && (
        <div className={elementClassName('talent-tree')}>
          <TalentTreeFull {...selected.talentTree} />
        </div>
      )}
      <div className={elementClassName('alternative-build-row')}>
        <div
          className={`${elementClassName(
            'alternative-build-column',
          )} ${elementClassName('alternative-build-column', 'labels')}`}
        >
          <span></span>
          <Heading level={6} as={'span'}>
            {labels.addedTalentsLabel}
          </Heading>
          <Heading level={6} as={'span'}>
            {labels.removedTalentsLabel}
          </Heading>
        </div>
        <div className={elementClassName('alternative-build-column', 'inset')}>
          <span className={'column-label'}>
            <EnhancedMarkdown markdown={labels.classTalentsLabel} />
          </span>
          <ChangedTalentsRow
            spellModifierId={blueprint?.spellModifierId ?? 0}
            talents={addedTalents?.class}
            labels={labels}
          />
          <ChangedTalentsRow
            spellModifierId={blueprint?.spellModifierId ?? 0}
            talents={removedTalents?.class}
            labels={labels}
          />
        </div>
        {includeHeroTreeColumn && (
          <div
            className={elementClassName('alternative-build-column', 'inset')}
          >
            <span className={'column-label'}>
              <EnhancedMarkdown
                markdown={`<ImageIcon size='36' src='${heroTreeBlueprint?.icon}'>${labels.heroTalentsLabel}</ImageIcon>`}
              />
            </span>
            <div
              className={elementClassName(
                'alternative-build-row',
                'content-hero',
              )}
            >
              {heroTreeId !== referenceHeroTreeId ? (
                <HeroTreeLabel
                  heroTreeId={heroTreeId}
                  blueprint={blueprint}
                  type={'added'}
                />
              ) : null}
              <ChangedTalentsRow
                spellModifierId={blueprint?.spellModifierId ?? 0}
                talents={addedTalents?.hero}
                labels={labels}
              />
            </div>
            <div
              className={elementClassName(
                'alternative-build-row',
                'content-hero',
              )}
            >
              {heroTreeId !== referenceHeroTreeId ? (
                <HeroTreeLabel
                  heroTreeId={referenceHeroTreeId}
                  blueprint={blueprint}
                  type={'removed'}
                />
              ) : null}
              <ChangedTalentsRow
                spellModifierId={blueprint?.spellModifierId ?? 0}
                talents={removedTalents?.hero}
                labels={labels}
              />
            </div>
          </div>
        )}
        <div className={elementClassName('alternative-build-column', 'inset')}>
          <span className={'column-label'}>
            <EnhancedMarkdown markdown={labels.specTalentsLabel} />
          </span>
          <ChangedTalentsRow
            spellModifierId={blueprint?.spellModifierId ?? 0}
            talents={addedTalents?.spec}
            labels={labels}
          />
          <ChangedTalentsRow
            spellModifierId={blueprint?.spellModifierId ?? 0}
            talents={removedTalents?.spec}
            labels={labels}
          />
        </div>
      </div>
      {showAlternateClassTrees ? (
        <BuildsTalentTreeAlternateClassTreesTable
          buildSet={buildSet}
          setSelectedIndex={setSelectedIndex}
          selected={selected}
          {...labels}
        />
      ) : (
        <Button
          styles={['link', 'bold']}
          onClick={() => setShowAlternateClassTrees(true)}
        >
          {labels.showClassTreesLabel}
        </Button>
      )}
    </VerticalContent>
  );
}

function ChangedTalentsRow({
  talents,
  labels,
  spellModifierId,
}: {
  talents?: RetailWarcraftTalentTreeBuildNode[];
  labels: AlternativeBuildLabels;
  spellModifierId: number;
}): JSX.Element {
  if (talents === undefined) {
    return <div />;
  }

  if (talents.length === 0) {
    return (
      <span className={elementClassName('changed-talent-row', 'empty')}>
        {labels.noneLabel}
      </span>
    );
  }

  return (
    <HorizontalContent
      className={elementClassName('changed-talent-row')}
      gap={'small'}
    >
      {talents
        .filter(
          (node) =>
            node.node.treeType !== RetailWarcraftTalentTreeType.HeroTree ||
            node.node.type === RetailWarcraftTalentTreeNodeType.Choice,
        )
        .map((node) => (
          <TalentTreeNode
            key={node.node.nodeId}
            node={node}
            spellModifierId={spellModifierId}
            showChoices={false}
          />
        ))}
    </HorizontalContent>
  );
}

function HeroTreeLabel({
  heroTreeId,
  blueprint,
  type,
}: {
  heroTreeId: number | null | undefined;
  blueprint: RetailWarcraftTalentTreeBlueprint | undefined;
  type: 'added' | 'removed';
}): JSX.Element | null {
  if (!heroTreeId || !blueprint) {
    return null;
  }

  const heroTreeBlueprint = blueprint.heroTrees?.find(
    (tree) => tree.id === heroTreeId,
  );
  if (!heroTreeBlueprint) {
    return null;
  }

  return (
    <Tooltip
      content={heroTreeBlueprint.name}
      offsetOptions={{
        mainAxis: 15,
      }}
    >
      <div className={'hero-tree-label'}>
        <Icon
          src={heroTreeBlueprint.icon}
          alt={heroTreeBlueprint.name}
          size={40}
          rounded
          bordered
          cssStyle={
            {
              '--border-color': type === 'added' ? 'green' : 'red',
              borderWidth: 2,
              boxSizing: 'border-box',
            } as React.CSSProperties
          }
        ></Icon>
      </div>
    </Tooltip>
  );
}
